import crypto from "node:crypto";
import fs from "node:fs/promises";
import os from "node:os";
import path, { join } from "node:path";
import {
	CONTENT_HASH_OFFSET,
	ENTRY_SIZE,
	getContentType,
	HEADER_SIZE,
	MAX_ASSET_COUNT,
	MAX_ASSET_SIZE,
	normalizeFilePath,
	PATH_HASH_OFFSET,
	PATH_HASH_SIZE,
} from "@cloudflare/workers-shared";
import {
	constructHeaders,
	constructRedirects,
} from "@cloudflare/workers-shared/utils/configuration/constructConfiguration";
import { parseHeaders } from "@cloudflare/workers-shared/utils/configuration/parseHeaders";
import { parseRedirects } from "@cloudflare/workers-shared/utils/configuration/parseRedirects";
import { parseStaticRouting } from "@cloudflare/workers-shared/utils/configuration/parseStaticRouting";
import {
	HEADERS_FILENAME,
	REDIRECTS_FILENAME,
} from "@cloudflare/workers-shared/utils/constants";
import {
	createAssetsIgnoreFunction,
	maybeGetFile,
} from "@cloudflare/workers-shared/utils/helpers";
import {
	HeadersSchema,
	RedirectsSchema,
} from "@cloudflare/workers-shared/utils/types";
import prettyBytes from "pretty-bytes";
import SCRIPT_ASSETS from "worker:assets/assets";
import SCRIPT_ASSETS_KV from "worker:assets/assets-kv";
import SCRIPT_ROUTER from "worker:assets/router";
import SCRIPT_RPC_PROXY from "worker:assets/rpc-proxy";
import { SharedBindings } from "../../workers";
import { getUserServiceName } from "../core";
import { getEnvBindingsOfType, ProxyNodeBinding } from "../shared";
import {
	ASSETS_KV_SERVICE_NAME,
	ASSETS_PLUGIN_NAME,
	ASSETS_SERVICE_NAME,
	ROUTER_SERVICE_NAME,
	RPC_PROXY_SERVICE_NAME,
} from "./constants";
import type { ParsedMiniflareWorkerConfig } from "../../config/schema";
import type { Service } from "../../runtime";
import type { Plugin } from "../shared";
import type { Logger, RouterConfig } from "@cloudflare/workers-shared";
import type { AssetConfig } from "@cloudflare/workers-shared/utils/types";

// Cache of temp directories created for missing asset directories, keyed by
// the Worker's name followed by the assets directory path. Prevents accumulating
// orphaned temp directories when getServices is called repeatedly
const tempDirCache = new Map<string, string>();

function resolveAssetDirectory(
	rootPath: string | undefined,
	directory: string
) {
	if (rootPath !== undefined && !path.isAbsolute(directory)) {
		return path.resolve(rootPath, directory);
	}
	return directory;
}

/**
 * Builds the router worker's `RouterConfig` from the parsed worker config,
 * mirroring `resolveAssetOptions` in `@cloudflare/deploy-helpers`:
 * - `has_user_worker` comes from the explicit `assets.hasUserWorker` flag
 *   (defaults to false). It can't be inferred from manifest presence because
 *   wrangler injects a placeholder script for assets-only workers.
 * - `run_worker_first: boolean` → `invoke_user_worker_ahead_of_assets`.
 * - `run_worker_first: string[]` → parsed `static_routing` rules.
 */
function resolveRouterConfig(
	config: ParsedMiniflareWorkerConfig
): RouterConfig {
	const routerConfig: RouterConfig = {
		has_user_worker: config.assets?.hasUserWorker ?? false,
	};

	const runWorkerFirst = config.assets?.runWorkerFirst;
	if (typeof runWorkerFirst === "boolean") {
		routerConfig.invoke_user_worker_ahead_of_assets = runWorkerFirst;
	} else if (Array.isArray(runWorkerFirst)) {
		routerConfig.static_routing = parseStaticRouting(runWorkerFirst);
	}

	return routerConfig;
}

export const ASSETS_PLUGIN: Plugin = {
	bindingTypeDescription: "Assets",
	async getBindings(options) {
		const [assetsBinding] = getEnvBindingsOfType(options.config, "assets");
		if (!assetsBinding) {
			return [];
		}
		const [bindingName] = assetsBinding;
		return [
			{
				// binding between User Worker and Asset Worker
				name: bindingName,
				service: {
					name: `${ASSETS_SERVICE_NAME}:${options.config.name}`,
				},
			},
		];
	},

	async getNodeBindings(options) {
		const [assetsBinding] = getEnvBindingsOfType(options.config, "assets");
		if (!assetsBinding) {
			return {};
		}
		return {
			[assetsBinding[0]]: new ProxyNodeBinding(),
		};
	},

	async getServices({ options, log }) {
		const assets = options.config.assets;
		if (!assets) {
			return [];
		}

		let assetDirectory = resolveAssetDirectory(
			options.dev?.rootPath,
			assets.directory
		);
		const directoryStats = await fs.stat(assetDirectory).catch((err) => {
			if (err?.code === "ENOENT") {
				return undefined;
			}
			throw err;
		});
		if (!directoryStats) {
			// If the assets directory doesn't exist yet (e.g. the build output
			// hasn't been generated), create an empty temp directory so that the
			// asset services can still start up with zero assets.
			// Reuse a previously created temp directory for this path to avoid
			// accumulating orphaned temp directories on repeated calls.
			const cacheKey = `${options.config.name}:${assetDirectory}`;
			const cached = tempDirCache.get(cacheKey);
			const cachedExists = cached
				? await fs.stat(cached).catch(() => undefined)
				: undefined;
			if (cached && cachedExists) {
				assetDirectory = cached;
			} else {
				assetDirectory = await fs.mkdtemp(
					path.join(os.tmpdir(), "miniflare-assets-")
				);
				tempDirCache.set(cacheKey, assetDirectory);
			}
		}

		const storageServiceName = `${ASSETS_PLUGIN_NAME}:storage`;
		const storageService: Service = {
			name: storageServiceName,
			disk: {
				path: assetDirectory,
				writable: true,
				allowDotfiles: true,
			},
		};

		const { encodedAssetManifest, assetsReverseMap } =
			await buildAssetManifest(assetDirectory);

		const redirectsFile = join(assetDirectory, REDIRECTS_FILENAME);
		const headersFile = join(assetDirectory, HEADERS_FILENAME);

		const redirectsContents = maybeGetFile(redirectsFile);
		const headersContents = maybeGetFile(headersFile);

		const assetParserLogger = {
			debug: (message) => log.debug(message),
			log: (message) => log.info(message),
			info: (message) => log.info(message),
			warn: (message) => log.warn(message),
			error: (error) => log.error(error),
		} satisfies Logger;

		let parsedRedirects: AssetConfig["redirects"] | undefined;
		if (redirectsContents !== undefined) {
			const redirects = parseRedirects(redirectsContents, {
				htmlHandling: assets.htmlHandling,
			});
			parsedRedirects = RedirectsSchema.parse(
				constructRedirects({
					redirects,
					redirectsFile,
					logger: assetParserLogger,
				}).redirects
			);
		}

		let parsedHeaders: AssetConfig["headers"] | undefined;
		if (headersContents !== undefined) {
			const headers = parseHeaders(headersContents);
			parsedHeaders = HeadersSchema.parse(
				constructHeaders({
					headers,
					headersFile,
					logger: assetParserLogger,
				}).headers
			);
		}

		const routerConfig = resolveRouterConfig(options.config);

		const assetConfig: AssetConfig = {
			compatibility_date: options.config.compatibilityDate,
			compatibility_flags: options.config.compatibilityFlags,
			html_handling: assets.htmlHandling,
			not_found_handling: assets.notFoundHandling,
			redirects: parsedRedirects,
			headers: parsedHeaders,
			debug: true,
			has_static_routing: Boolean(routerConfig.static_routing),
		};

		const id = options.config.name;

		const namespaceService: Service = {
			name: `${ASSETS_KV_SERVICE_NAME}:${id}`,
			worker: {
				compatibilityDate: "2023-07-24",
				compatibilityFlags: ["nodejs_compat"],
				modules: [
					{
						name: "assets-kv-worker.mjs",
						esModule: SCRIPT_ASSETS_KV(),
					},
				],
				bindings: [
					{
						name: SharedBindings.MAYBE_SERVICE_BLOBS,
						service: { name: storageServiceName },
					},
					{
						name: "ASSETS_REVERSE_MAP",
						json: JSON.stringify(assetsReverseMap),
					},
				],
			},
		};

		const assetService: Service = {
			name: `${ASSETS_SERVICE_NAME}:${id}`,
			worker: {
				// TODO: read these from the wrangler.toml
				compatibilityDate: "2024-07-31",
				compatibilityFlags: ["nodejs_compat", "enable_ctx_exports"],
				modules: [
					{
						name: "asset-worker.mjs",
						esModule: SCRIPT_ASSETS(),
					},
				],
				bindings: [
					{
						name: "ASSETS_KV_NAMESPACE",
						kvNamespace: {
							name: `${ASSETS_KV_SERVICE_NAME}:${id}`,
						},
					},
					{
						name: "ASSETS_MANIFEST",
						data: encodedAssetManifest,
					},
					{
						name: "CONFIG",
						json: JSON.stringify(assetConfig),
					},
				],
			},
		};

		const routerService: Service = {
			name: `${ROUTER_SERVICE_NAME}:${id}`,
			worker: {
				// TODO: read these from the wrangler.toml
				compatibilityDate: "2024-07-31",
				compatibilityFlags: [
					"nodejs_compat",
					"no_nodejs_compat_v2",
					"enable_ctx_exports",
				],
				modules: [
					{
						name: "router-worker.mjs",
						esModule: SCRIPT_ROUTER(),
					},
				],
				bindings: [
					{
						name: "ASSET_WORKER",
						service: {
							name: `${ASSETS_SERVICE_NAME}:${id}`,
						},
					},
					{
						name: "USER_WORKER",
						service: { name: getUserServiceName(id) },
					},
					{
						name: "CONFIG",
						json: JSON.stringify(routerConfig),
					},
				],
			},
		};

		const assetsProxyService: Service = {
			name: `${RPC_PROXY_SERVICE_NAME}:${id}`,
			worker: {
				compatibilityDate: "2024-08-01",
				compatibilityFlags: ["service_binding_extra_handlers"],
				modules: [
					{
						name: "assets-proxy-worker.mjs",
						esModule: SCRIPT_RPC_PROXY(),
					},
				],
				bindings: [
					{
						name: "ROUTER_WORKER",
						service: {
							name: `${ROUTER_SERVICE_NAME}:${id}`,
						},
					},
					{
						name: "USER_WORKER",
						service: {
							name: getUserServiceName(id),
						},
					},
				],
			},
		};

		return [
			storageService,
			namespaceService,
			assetService,
			routerService,
			assetsProxyService,
		];
	},
};

/**
 * The Asset Manifest and Asset Reverse Map are used to map a request path to an asset.
 * 1. Hash path of request
 * 2. Use this path hash to find the manifest entry
 * 3. Get content hash from manifest entry
 * 4a. In prod, use content hash to get asset from KV
 * 4b. In dev, we fake out the KV store and use the file system instead.
 * 	   Use content hash to get file path from asset reverse map.
 */

export const buildAssetManifest = async (dir: string) => {
	const { manifest, assetsReverseMap } = await walk(dir);
	const sortedAssetManifest = sortManifest(manifest);
	const encodedAssetManifest = encodeManifest(sortedAssetManifest);
	return { encodedAssetManifest, assetsReverseMap };
};

export type ManifestEntry = {
	pathHash: Uint8Array;
	contentHash: Uint8Array;
};

export type AssetReverseMap = {
	[pathHash: string]: { filePath: string; contentType: string | null };
};

/**
 * Traverses the asset directory to create an asset manifest and asset reverse map.
 * These are available to the Asset Worker as a binding.
 * NB: This runs every time the dev server restarts.
 */
const walk = async (dir: string) => {
	const files = await fs.readdir(dir, { recursive: true });
	const manifest: ManifestEntry[] = [];
	const assetsReverseMap: AssetReverseMap = {};
	const { assetsIgnoreFunction } = await createAssetsIgnoreFunction(dir);
	let counter = 0;
	await Promise.all(
		files.map(async (file) => {
			if (assetsIgnoreFunction(file)) {
				return;
			}
			/** absolute file path */
			const filepath = path.join(dir, file);
			const relativeFilepath = path.relative(dir, filepath);
			const filestat = await fs.stat(filepath);

			// TODO: decide whether to follow symbolic links
			if (filestat.isSymbolicLink() || filestat.isDirectory()) {
				return;
			} else {
				// TODO: Warn about _worker.js

				if (filestat.size > MAX_ASSET_SIZE) {
					throw new Error(
						`Asset too large.\n` +
							`Cloudflare Workers supports assets with sizes of up to ${prettyBytes(
								MAX_ASSET_SIZE,
								{
									binary: true,
								}
							)}. We found a file ${filepath} with a size of ${prettyBytes(
								filestat.size,
								{
									binary: true,
								}
							)}.\n` +
							`Ensure all assets in your assets directory "${dir}" conform with the Workers maximum size requirement.`
					);
				}

				/*
				 * Each manifest entry is a series of bytes that store data in a [header,
				 * pathHash, contentHash] format, where:
				 *   - `header` is a fixed 20 bytes reserved but currently unused
				 *   - `pathHash` is the hashed file path
				 *   - `contentHash` is the hashed file content in prod
				 *
				 * The `contentHash` of a file is determined by reading the contents of
				 * the file, and applying a hash function on the read data. In local
				 * development, performing this operation for each asset file would
				 * become very expensive very quickly, as it would have to be performed
				 * every time a dev server reload is trigerred. In watch mode, depending
				 * on the user's setup, this could potentially be on every file change.
				 *
				 * To avoid this from becoming a performance bottleneck, we're doing
				 * things a bit differently for dev, and implementing the `contentHash`
				 * as a hash function of the file path and the modified timestamp.
				 * (`hash(filePath + modifiedTime)`).
				 * This way a file's corresponding 'contentHash' will always update
				 * if the file changes, and `wrangler dev` will serve the updated asset
				 * files instead of incorrectly returning 304s.
				 */

				const [pathHash, contentHash] = await Promise.all([
					hashPath(normalizeFilePath(relativeFilepath)),
					// used absolute filepath here so that changes to the enclosing asset folder will be registered
					hashPath(filepath + filestat.mtimeMs.toString()),
				]);
				manifest.push({
					pathHash,
					contentHash,
				});
				assetsReverseMap[bytesToHex(contentHash)] = {
					filePath: relativeFilepath,
					contentType: getContentType(filepath),
				};
				counter++;
			}
		})
	);
	if (counter > MAX_ASSET_COUNT) {
		throw new Error(
			`Maximum number of assets exceeded.\n` +
				`Cloudflare Workers supports up to ${MAX_ASSET_COUNT.toLocaleString()} assets in a version. We found ${counter.toLocaleString()} files in the specified assets directory "${dir}".\n` +
				`Ensure your assets directory contains a maximum of ${MAX_ASSET_COUNT.toLocaleString()} files, and that you have specified your assets directory correctly.`
		);
	}
	return { manifest, assetsReverseMap };
};

// sorts ascending by path hash
const sortManifest = (manifest: ManifestEntry[]) => {
	return manifest.sort(comparisonFn);
};

const comparisonFn = (a: ManifestEntry, b: ManifestEntry) => {
	if (a.pathHash.length < b.pathHash.length) {
		return -1;
	}
	if (a.pathHash.length > b.pathHash.length) {
		return 1;
	}
	for (const [i, v] of a.pathHash.entries()) {
		if (v < b.pathHash[i]) {
			return -1;
		}
		if (v > b.pathHash[i]) {
			return 1;
		}
	}
	return 1;
};

const encodeManifest = (manifest: ManifestEntry[]) => {
	const assetManifestBytes = new Uint8Array(
		HEADER_SIZE + manifest.length * ENTRY_SIZE
	);

	for (const [i, entry] of manifest.entries()) {
		const entryOffset = HEADER_SIZE + i * ENTRY_SIZE;
		assetManifestBytes.set(entry.pathHash, entryOffset + PATH_HASH_OFFSET);
		// content hash here in dev is hash(path + last modified time of file)
		assetManifestBytes.set(
			entry.contentHash,
			entryOffset + CONTENT_HASH_OFFSET
		);
	}
	return assetManifestBytes;
};

const bytesToHex = (buffer: Uint8Array<ArrayBuffer>) => {
	return [...new Uint8Array(buffer)]
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
};

const hashPath = async (path: string) => {
	const encoder = new TextEncoder();
	const data = encoder.encode(path);
	const hashBuffer = await crypto.subtle.digest(
		"SHA-256",
		data.buffer as ArrayBuffer
	);
	return new Uint8Array(hashBuffer, 0, PATH_HASH_SIZE);
};
